/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as AgeVerificationActionCreators from '@app/actions/AgeVerificationActionCreators';
import * as ModalActionCreators from '@app/actions/ModalActionCreators';
import * as Modal from '@app/components/modals/Modal';
import {Button} from '@app/components/uikit/button/Button';
import {openExternalUrl} from '@app/utils/NativeUtils';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useCallback, useRef, useState} from 'react';

export const AgeVerificationModal = observer(() => {
	const {t} = useLingui();
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const initialFocusRef = useRef<HTMLButtonElement | null>(null);

	const handleVerify = useCallback(async () => {
		setSubmitting(true);
		setError(null);
		try {
			const url = await AgeVerificationActionCreators.createVerificationSession();
			ModalActionCreators.pop();
			await openExternalUrl(url);
		} catch {
			setError(t`Something went wrong. Please try again.`);
		} finally {
			setSubmitting(false);
		}
	}, [t]);

	const handleCancel = useCallback(() => {
		ModalActionCreators.pop();
	}, []);

	return (
		<Modal.Root size="small" initialFocusRef={initialFocusRef} centered>
			<Modal.Header title={t`Verify Your Age`} />
			<Modal.Content>
				<Modal.ContentLayout>
					<Modal.Description>
						<Trans>
							UK law requires age verification for adult content. To verify your age, you'll complete a $0.00 credit
							card authorisation through Stripe. Your card will not be charged.
						</Trans>
					</Modal.Description>
					<Modal.Description>
						<Trans>Only credit cards are accepted. Debit and prepaid cards cannot be used for age verification.</Trans>
					</Modal.Description>
					{error != null && <Modal.Description>{error}</Modal.Description>}
				</Modal.ContentLayout>
			</Modal.Content>
			<Modal.Footer>
				<Button onClick={handleCancel} variant="secondary">
					<Trans>Cancel</Trans>
				</Button>
				<Button onClick={handleVerify} submitting={submitting} variant="primary" ref={initialFocusRef}>
					<Trans>Verify with Credit Card</Trans>
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
});
